"use client";

import Link from "next/link";
import { Check } from "lucide-react";

type PricingCardProps = {
  title: string;
  priceFull: string;
  priceCents: string;
  details: string;
  details2?: string;
  features: string[];
  periodLabel?: string;
};

const buildSupportUrl = (produkt: string, preis: string) =>
  `/support?produkt=${encodeURIComponent(produkt)}&preis=${encodeURIComponent(preis)}`;

export const PricingCard = ({
  title,
  priceFull,
  priceCents,
  details,
  details2,
  features,
  periodLabel = "monatlich",
}: PricingCardProps) => {
  const priceString = `${priceFull},${priceCents} € / ${periodLabel}`;
  const supportUrl = buildSupportUrl(title, priceString);

  return (
    <Link href={supportUrl} className="group block">
      <div className="bg-background rounded-sm px-7 py-5 hover:shadow-lg transition-all duration-300 flex flex-col min-h-105">
        <h2 className="text-4xl mb-6">{title}</h2>

        <div className="flex flex-row">
          <p className="font-body md:tracking-tighter m-0 text-[90px] leading-22.5">
            {priceFull}
          </p>
          <div className="font-body flex flex-col ml-4">
            <p className="m-0 text-2xl leading-14.25 sm:text-[32px] grow">
              ,{priceCents}
            </p>
            <p className="m-0 font-bold leading-9 text-md sm:text-xl">
              € {periodLabel}
            </p>
          </div>
        </div>

        <p className="text-grey text-sm mt-1">{details}</p>
        <p className="text-grey text-sm mt-1">{details2}</p>

        <div className="mt-4 text-grey">
          <ul className="space-y-1">
            {features.map((feature, index) => (
              <li key={index}>
                <Check className="inline text-brand-primary" /> {feature}
              </li>
            ))}
          </ul>
        </div>

        <span className="mt-10 pt-5 inline-flex items-center justify-center bg-brand-primary text-white py-5 px-10 rounded-full font-bold text-sm tracking-widest transition-all hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl">
          Unverbindlich anfragen
        </span>
      </div>
    </Link>
  );
};

export default function PricingSection() {
  return (
    <section className="bg-white py-24">
      <div className="max-w-3xl mx-auto text-center px-4 mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900">
          Wähle dein passendes Finanzierungsmodell
        </h2>
        <p className="mt-4 text-lg text-slate-600">
          Flexible Modelle für jedes Budget - entscheide zwischen Leasing, Miete
          oder Kauf und finde die passende Lösung für Dein Geschäft.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
        <PricingCard
          title="Leasing ab"
          priceFull="24"
          priceCents="90"
          details="Basierend auf 0,83 € / Tag."
          details2="36 / 48 / 60 Monate Laufzeit."
          features={[
            "Warenwirtschaft ModeProfi / CashTex",
            "Voller Support & Updates & Schulungen",
            "Inklusive Kassenbuch",
            "Angebote, Order & Rechnungen",
            "1 Benutzer",
          ]}
        />

        <PricingCard
          title="Mieten ab"
          priceFull="48"
          priceCents="90"
          details="Basierend auf 1,63 € / Tag."
          details2="36 / 48 / 60 Monate Laufzeit."
          features={[
            "Warenwirtschaft ModeProfi / CashTex",
            "Voller Support & Updates & Schulungen",
            "Inklusive Kassenbuch",
            "Ideal für wachsende Unternehmen",
            "1 Benutzer",
          ]}
        />

        <PricingCard
          title="Ratenkauf ab"
          priceFull="74"
          priceCents="90"
          details="Basierend auf 2,49 € / Tag."
          details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
          features={[
            "Warenwirtschaft ModeProfi / CashTex",
            "Voller Support & Updates & Schulungen",
            "Inklusive Kassenbuch",
            "Eigentumserwerb nach Laufzeit",
            "1 Benutzer",
          ]}
        />
      </div>

      <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
        Alle Preise exkl. MwSt.
      </div>
    </section>
  );
}
